import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, ScrollView, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { getImageUrlSync } from '../utils/imageProxy';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import { useConversationHandler } from '../hooks/useConversationHandler';
import { getCallStatusLabel, getCallStatusColor } from '../services/phone-dialer.service';
import { NotesSection, Note } from './NotesSection';
import { ContactQuickActions } from './ContactQuickActions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';
import { useCallSpeaker } from '../hooks/useCallSpeaker';
import { useLocale } from '../contexts/LocaleContext';
import { firstText } from '../utils/contactIdentifiers';

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const getInitials = (name: string): string =>
  name.split(' ').map((n) => n.charAt(0)).join('').substring(0, 2).toUpperCase();

// Mirrors ContactsList.tsx's getProviderIcon/getProviderColor — kept local
// rather than shared since ProviderIndicator.tsx's MessagingProvider union
// doesn't cover 'email', and this is a small enough pure lookup to duplicate
// rather than widen a shared type used elsewhere.
const getChannelIcon = (channel: string): any => {
  switch (channel) {
    case 'whatsapp': return 'logo-whatsapp';
    case 'facebook-messenger': return 'logo-facebook';
    case 'telegram': return 'paper-plane';
    case 'email': return 'mail';
    case 'x-twitter': return 'logo-twitter';
    case 'instagram': return 'logo-instagram';
    case 'phone': return 'call';
    default: return 'chatbubble';
  }
};

const getChannelColor = (channel: string): string => {
  switch (channel) {
    case 'whatsapp': return '#25D366';
    case 'facebook-messenger': return '#1877F2';
    case 'telegram': return '#0088CC';
    case 'email': return '#EA4335';
    case 'x-twitter': return '#000000';
    case 'instagram': return '#E4405F';
    case 'phone': return '#0EA5E9';
    default: return colors.primary as string;
  }
};

// This screen is entirely English-hardcoded today (no i18n hook wired in —
// see "Dismiss"/"Call Back"/"End Call" below), so this matches that existing
// convention rather than introducing translation scope on its own.
const getChannelDisplayName = (channel: string): string => {
  switch (channel) {
    case 'whatsapp': return 'WhatsApp';
    case 'facebook-messenger': return 'Messenger';
    case 'telegram': return 'Telegram';
    case 'email': return 'Email';
    case 'x-twitter': return 'X (Twitter)';
    case 'instagram': return 'Instagram';
    case 'phone': return 'Phone';
    default: return channel;
  }
};

// Channels that open an existing conversation in the middle pane when
// tapped. Restricted to what the backend's createOrFindConversation
// endpoint actually supports today (confirmed via
// omni-channel.service.ts's findConversationByContact/createConversation —
// both switch only on 'whatsapp'/'facebook-messenger', defaulting to a
// silent no-op otherwise). X-Twitter has its own conversation model
// elsewhere in the backend but isn't wired into this endpoint yet;
// Instagram has no conversation model in this codebase at all. Both icons
// still render (per the design) but are non-interactive until that's
// addressed, rather than silently failing when tapped.
const OPENABLE_CHANNELS = new Set(['whatsapp', 'facebook-messenger']);

const MAX_RECENT_NOTES = 3;

/**
 * Full in-call screen hosted inside RightPane — replaces the old floating
 * PhoneCallBar. Layout: upper section (avatar/name/status, InnoCalls-mapped),
 * middle section (larger clickable channel identifier icons + recent notes
 * with quick-add), fixed "end call" footer. No InnoCalls UI is ever shown,
 * per product requirement — this is Sendifier's own screen driving the
 * headless dialer via AppContext.
 */
export const PhoneCallScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const callAudio = useCallSpeaker();
  const { t } = useLocale();
  const {
    phoneCallState,
    phoneCallDuration,
    phoneCallContact,
    hangupPhoneCall,
    setPhoneCallMinimized,
    startPhoneCall,
    dismissPhoneCall,
    isPhoneDialerReady,
    showNotification,
  } = useAppContext();
  const { user } = useAppStore();
  const { startConversation } = useConversationHandler();

  const isConnected = phoneCallState === 'connected';
  // Every terminal outcome (completed/ended/cancelled/not-responded/failed)
  // persists on screen until the user explicitly dismisses or calls back —
  // none of them auto-clear, since a completed call still has actionable
  // post-call quick actions (reminder / add to list / change assignment).
  const isTerminal =
    phoneCallState === 'completed' ||
    phoneCallState === 'ended' ||
    phoneCallState === 'cancelled' ||
    phoneCallState === 'not-responded' ||
    phoneCallState === 'failed';
  const apiBaseURL = apiService.getBaseURL?.() ?? null;

  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => {
    setAvatarFailed(false);
  }, [phoneCallContact?.avatar]);

  const name = phoneCallContact?.name || 'Unknown';
  const contactId = phoneCallContact?.contactId;

  // Recent notes (top 3) + quick-add, loaded fresh per call via the contact's
  // omni-contact id — same underlying data/endpoints as ConversationScreen's
  // Notes panel (getParentContactById's nested notes / addContactNote).
  const [notes, setNotes] = useState<Note[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // The number actually being dialed, for display — deliberately NOT
  // phoneCallContact.phoneNumber/dialTarget (those are always the real,
  // unmasked value the dialer needs regardless of contacts.viewMobileNumber,
  // same rationale as getDialTarget's own doc comment). Resolved by matching
  // the real dialed digits against this SAME getParentContactById response
  // already fetched for notes above — that response IS masked server-side
  // (canViewMobileNumber) — so a caller without permission sees the masked
  // form of the actual number being called, not a fixed/default identifier.
  const [displayedCallNumber, setDisplayedCallNumber] = useState<string | null>(null);

  // Raw linked-channel entries (one per linked identity, e.g. two WhatsApp
  // numbers each get their own entry) from the SAME getParentContactById
  // response already fetched below — used to render ONE icon per channel
  // TYPE (not one per entry) with a picker for types that have more than
  // one, mirroring ContactsList.tsx's/ContactEditForm.tsx's identical
  // channel-icon pickers. phoneCallContact.linkedChannels (a flat channel[]
  // string array with a duplicate per linked entry) was the wrong data
  // shape for this — it has no channelContactId/channelIdentifier per entry
  // to distinguish or target one specifically, and mapping it directly
  // rendered a literal duplicate WhatsApp icon for a two-number contact.
  const [linkedChannelContacts, setLinkedChannelContacts] = useState<any[]>([]);
  const [channelIconPickerType, setChannelIconPickerType] = useState<string | null>(null);

  const loadRecentNotes = useCallback(async (id: string) => {
    setLoadingNotes(true);
    try {
      const result = await apiService.getParentContactById(id);
      if (result.statusCode === 1000 && result.data) {
        const allNotes: Note[] = result.data.notes || [];
        const sorted = [...allNotes].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setNotes(sorted.slice(0, MAX_RECENT_NOTES));

        // Full-digit match FIRST — NOT a truncated last-N-digits comparison,
        // which silently mismatched whenever two of the contact's real
        // numbers happened to share the same trailing digits (confirmed
        // real bug: a contact with numbers A and B always displayed B even
        // when A was the one actually dialed). Falls back to a suffix match
        // (one fully contains the other's digits) only to tolerate a
        // leading '+'/country-code or leading-zero formatting difference
        // between the dialed value and how identifiers.phone[] stores it —
        // never a fixed-length truncation, which is what caused the
        // original collision.
        const dialedDigits = firstText(phoneCallContact?.phoneNumber).replace(/\D/g, '');
        const rawPhones: string[] = result.data.identifiers?.phone || [];
        let matchedIndex = dialedDigits
          ? rawPhones.findIndex((p: string) => p.replace(/\D/g, '') === dialedDigits)
          : -1;
        if (matchedIndex === -1 && dialedDigits) {
          matchedIndex = rawPhones.findIndex((p: string) => {
            const d = p.replace(/\D/g, '');
            return d.length > 0 && (dialedDigits.endsWith(d) || d.endsWith(dialedDigits));
          });
        }
        // Last resort: the real, already-correct dialed value itself —
        // never the possibly-stale profile.phone (overwritten by whichever
        // WhatsApp number most recently linked, independent of what this
        // specific call actually dialed).
        setDisplayedCallNumber(matchedIndex >= 0 ? rawPhones[matchedIndex] : (phoneCallContact?.phoneNumber || null));
        setLinkedChannelContacts(result.data.linkedChannelContacts || []);
      }
    } catch (error) {
      console.error('[PhoneCallScreen] Failed to load contact notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, [phoneCallContact?.phoneNumber]);

  useEffect(() => {
    if (contactId) {
      loadRecentNotes(contactId);
    } else {
      setNotes([]);
      // No linked contact (e.g. a raw manual dial) — nothing to mask against,
      // so just show the dialed number as-is.
      setDisplayedCallNumber(phoneCallContact?.phoneNumber || null);
      setLinkedChannelContacts([]);
    }
  }, [contactId, loadRecentNotes, phoneCallContact?.phoneNumber]);

  const handleAddNote = useCallback(async (content: string) => {
    if (!contactId) return;
    try {
      const result = await apiService.addContactNote(contactId, content);
      if (result.statusCode === 1000 && result.data) {
        const allNotes: Note[] = result.data.notes || [];
        const sorted = [...allNotes].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setNotes(sorted.slice(0, MAX_RECENT_NOTES));
      }
    } catch (error) {
      console.error('[PhoneCallScreen] Failed to add note:', error);
    }
  }, [contactId]);

  const handleChannelPress = useCallback((channel: string, channelIdentifier?: string) => {
    if (!OPENABLE_CHANNELS.has(channel) || !contactId) return;
    // createOrFindConversation looks up by contactId first, but that's the
    // omni-contact (parent) id — not the WhatsApp-specific linked-channel
    // contact id — so it commonly misses, and needs the phoneNumber fallback
    // lookup to actually find the conversation (see omni-channel.service.ts's
    // findConversationByPhoneNumber). Without it the icon looked clickable
    // but silently failed to open anything.
    // channelIdentifier, when passed (a specific entry chosen from the
    // picker below), targets THAT linked number rather than defaulting to
    // whatever number was actually dialed — a contact can legitimately be
    // called on a different number than the one it's messaged on.
    startConversation({
      id: contactId,
      name,
      channel: channel as 'whatsapp' | 'facebook-messenger' | 'telegram' | 'email' | 'instagram' | 'x-twitter',
      phoneNumber: channelIdentifier || phoneCallContact?.phoneNumber,
    });
  }, [contactId, name, phoneCallContact?.phoneNumber, startConversation]);

  // Tap on a channel icon that has more than one linked identifier (e.g. two
  // WhatsApp numbers) — opens a picker instead of always acting on the
  // first-linked one, mirroring ContactsList.tsx's/ContactEditForm.tsx's
  // identical channel-icon pickers.
  const handleChannelIconPress = useCallback((channel: string, hasMultiple: boolean) => {
    if (hasMultiple) {
      setChannelIconPickerType(channel);
    } else {
      handleChannelPress(channel);
    }
  }, [handleChannelPress]);

  const handleCallBack = useCallback(() => {
    if (!phoneCallContact?.phoneNumber) return;
    // Same readiness guard as ContactsList/ConversationScreen's call buttons.
    // No masking risk here — phoneCallContact.phoneNumber is whatever real
    // number was already resolved (via getContactDialTarget) for the call
    // that just ended, not read fresh off a possibly-masked contact field.
    if (!isPhoneDialerReady) {
      console.warn('[PhoneCallScreen] Call-back pressed before phone dialer is ready — ignoring press');
      return;
    }
    startPhoneCall(phoneCallContact.phoneNumber, contactId, {
      name: phoneCallContact.name,
      avatar: phoneCallContact.avatar,
      linkedChannels: phoneCallContact.linkedChannels,
    });
  }, [phoneCallContact, contactId, startPhoneCall, isPhoneDialerReady]);

  return (
    <View style={styles.container}>
      {/* Upper section — avatar, name, status */}
      <View style={styles.upperSection}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.minimizeButton}
            onPress={() => setPhoneCallMinimized(true)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="chevron-down" size={20} color={colors.text.secondary as string} />
          </TouchableOpacity>
        </View>

        {phoneCallContact?.avatar && !avatarFailed ? (
          <Image
            source={{ uri: getImageUrlSync(phoneCallContact.avatar, apiBaseURL) }}
            style={styles.avatar}
            onError={() => setAvatarFailed(true)}
          />
        ) : (
          <View style={[styles.avatar, styles.avatarFallback]}>
            <Text style={styles.avatarInitials}>{getInitials(name)}</Text>
          </View>
        )}

        <Text style={styles.name} numberOfLines={1}>{name}</Text>
        {!!displayedCallNumber && (
          <Text style={styles.callNumber} numberOfLines={1}>{displayedCallNumber}</Text>
        )}

        <View style={styles.statusRow}>
          <View style={[styles.statusDot, { backgroundColor: getCallStatusColor(phoneCallState) }]} />
          <Text style={styles.statusText}>
            {isConnected ? formatDuration(phoneCallDuration) : getCallStatusLabel(phoneCallState)}
          </Text>
        </View>
      </View>

      {!isTerminal && callAudio.available && <View style={{ alignItems: 'center', padding: 12 }}>
        <TouchableOpacity onPress={callAudio.toggle} disabled={callAudio.pending}
          accessibilityRole="button" accessibilityLabel={t('phone.audio.speaker')}
          accessibilityState={{ selected: callAudio.speaker, disabled: callAudio.pending }}
          style={{ padding: 14, borderRadius: 28, backgroundColor: callAudio.speaker ? colors.primary as string : colors.background.secondary as string }}>
          <Ionicons name={callAudio.speaker ? 'volume-high' : 'ear-outline'} size={26} color={callAudio.speaker ? '#fff' : colors.text.primary as string} />
          <Text style={{ color: callAudio.speaker ? '#fff' : colors.text.primary as string }}>{t(callAudio.speaker ? 'phone.audio.speakerOn' : 'phone.audio.speakerOff')}</Text>
        </TouchableOpacity>
        {!!callAudio.error && <Text accessibilityRole="alert" style={{ color: colors.status.error as string }}>{callAudio.error}</Text>}
      </View>}

      {/* Middle section — larger identifier icons + notes */}
      <ScrollView style={styles.middleSection} contentContainerStyle={styles.middleSectionContent}>
        {linkedChannelContacts.length > 0 && (
          <View style={styles.channelsRow}>
            {Object.entries(
              linkedChannelContacts.reduce((acc: Record<string, any[]>, lc: any) => {
                (acc[lc.channel] = acc[lc.channel] || []).push(lc);
                return acc;
              }, {})
            ).map(([channel, group]) => {
              const openable = OPENABLE_CHANNELS.has(channel) && !!contactId;
              const hasMultiple = group.length > 1;
              return (
                <TouchableOpacity
                  key={channel}
                  style={[styles.channelIconWrap, { backgroundColor: getChannelColor(channel) + '20' }]}
                  onPress={() => handleChannelIconPress(channel, hasMultiple)}
                  disabled={!openable && !hasMultiple}
                  activeOpacity={openable || hasMultiple ? 0.7 : 1}
                >
                  <Ionicons name={getChannelIcon(channel)} size={22} color={getChannelColor(channel)} />
                  {hasMultiple && (
                    <View style={styles.channelIconMultiBadge}>
                      <Ionicons name="chevron-down" size={8} color={colors.white} />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {!!contactId && (
          <ContactQuickActions
            contactId={contactId}
            contactName={name}
            phoneNumber={phoneCallContact?.phoneNumber}
            onNotify={showNotification}
          />
        )}

        <View style={styles.notesWrapper}>
          <NotesSection
            notes={notes}
            onAddNote={handleAddNote}
            currentUserId={user?.id}
            title="Recent Notes"
            editable={!!contactId}
            compact
          />
          {loadingNotes && notes.length === 0 && (
            <Text style={styles.notesLoadingText}>Loading notes…</Text>
          )}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        {isTerminal ? (
          <View style={styles.terminalActionsRow}>
            <TouchableOpacity style={styles.dismissButton} onPress={dismissPhoneCall} activeOpacity={0.75}>
              <Ionicons name="close" size={18} color={colors.text.secondary as string} />
              <Text style={styles.dismissButtonText}>Dismiss</Text>
            </TouchableOpacity>
            {!!phoneCallContact?.phoneNumber && (
              <TouchableOpacity style={styles.callBackButton} onPress={handleCallBack} activeOpacity={0.85}>
                <Ionicons name="call" size={18} color="#FFFFFF" />
                <Text style={styles.callBackButtonText}>Call Back</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <TouchableOpacity style={styles.endCallButton} onPress={hangupPhoneCall} activeOpacity={0.85}>
            <Ionicons name="call" size={22} color="#FFFFFF" style={{ transform: [{ rotate: '135deg' }] }} />
            <Text style={styles.endCallText}>End Call</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Channel icon picker — opens when a type (e.g. WhatsApp) has more
          than one linked identifier, mirroring ContactsList.tsx's/
          ContactEditForm.tsx's identical channel-icon pickers. Anchored
          below the icon row with a fixed offset rather than the tapped
          icon's own touch coordinates — this panel is a short, fixed-size
          layout (not a long scrollable list), so there's no scroll-offset
          concern to account for. */}
      <Modal
        transparent
        visible={!!channelIconPickerType}
        animationType="fade"
        onRequestClose={() => setChannelIconPickerType(null)}
      >
        <TouchableOpacity
          style={styles.iconPickerBackdrop}
          activeOpacity={1}
          onPress={() => setChannelIconPickerType(null)}
        >
          <View style={styles.iconPickerContainer}>
            <Text style={styles.iconPickerTitle}>
              {channelIconPickerType ? getChannelDisplayName(channelIconPickerType) : ''}
            </Text>
            {linkedChannelContacts
              .filter((lc: any) => lc.channel === channelIconPickerType)
              .map((lc: any, index: number) => (
                <TouchableOpacity
                  key={lc.channelContactId || index}
                  style={styles.iconPickerRow}
                  onPress={() => {
                    setChannelIconPickerType(null);
                    handleChannelPress(lc.channel, lc.channelIdentifier);
                  }}
                >
                  <Ionicons name={getChannelIcon(lc.channel)} size={14} color={getChannelColor(lc.channel)} />
                  <Text style={styles.iconPickerRowText} numberOfLines={1}>{lc.channelIdentifier}</Text>
                  {lc.isPrimary && <Text style={styles.iconPickerPrimaryTag}>Primary</Text>}
                </TouchableOpacity>
              ))}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: spacing.sm,
  },
  minimizeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary as string,
  },
  upperSection: {
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    marginBottom: spacing.sm,
  },
  avatarFallback: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitials: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold' as const,
    color: colors.primary as string,
  },
  name: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
    maxWidth: '100%',
  },
  callNumber: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    marginBottom: spacing.xs,
    maxWidth: '100%',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  statusText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    fontVariant: ['tabular-nums'],
  },
  middleSection: {
    flex: 1,
  },
  middleSectionContent: {
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.sm,
  },
  channelsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  channelIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  channelIconMultiBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#FFFFFF',
  },
  notesWrapper: {
    flex: 1,
  },
  notesLoadingText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
    textAlign: 'center',
  },
  footer: {
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border as string,
  },
  endCallButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EF4444',
    borderRadius: 14,
    paddingVertical: spacing.md,
    gap: spacing.sm,
    shadowColor: '#EF4444',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 3,
  },
  endCallText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
    letterSpacing: 0.3,
  },
  terminalActionsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dismissButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: colors.border as string,
    backgroundColor: colors.background.surface as string,
    gap: spacing.xs,
  },
  dismissButtonText: {
    color: colors.text.secondary as string,
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
  },
  callBackButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary as string,
    borderRadius: 14,
    paddingVertical: spacing.md,
    gap: spacing.sm,
    shadowColor: colors.primary as string,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 3,
  },
  callBackButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
  },
  iconPickerBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconPickerContainer: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    padding: spacing.sm,
    minWidth: 200,
    maxWidth: 260,
  },
  iconPickerTitle: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600' as const,
    color: colors.text.secondary as string,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  iconPickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs / 2,
  },
  iconPickerRowText: {
    flex: 1,
    fontSize: 13,
    color: colors.text.primary as string,
  },
  iconPickerPrimaryTag: {
    fontSize: 10,
    fontWeight: '500' as const,
    color: colors.primary as string,
  },
});
